import DownloadIcon from '@mui/icons-material/Download';
import { Spinner } from '../../common/Spinner/Spinner';
import React, { useEffect, useRef, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers';
import { Button, MenuItem, Select, SelectChangeEvent, TextField, Tooltip } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import InputSearch from '../../common/InputSearch';
import { calendarStyleEnabled, selectStyle } from './muiStyles/muiStyles';
import ErrorResponse from '../../shared/ErrorResponse';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '../../../icons/arrow_back.svg';
import ArrowForwardIcon from '../../../icons/arrow_forward.svg';

import styles from './AuditTrail.module.scss';
import AuditTrailReportTable from '../../common/AuditTrailReportTable';
import { useDispatch, useSelector } from 'react-redux';
import { getAuditTrailReportRequest, getGlobalExcelReportRequest } from './services/actions';
import { audittrailReportSelector } from './services/selector';

export const AuditTrail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let timeoutId: NodeJS.Timeout | null = null;

  const { t } = useTranslation();

  const [moduleType, setModuleType] = useState<number>(0);
  const [calendarDate, setCalendarDate] = useState<any>(new Date());

  const firstRenderRef = useRef(true);

  const handleMonthChange = (increment: number) => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setCalendarDate(newDate);
  };

  const changeDateCalendar = (calendarValue: any) => {
    const newDate = new Date(calendarValue);
    newDate.setMonth(newDate.getMonth());
    setCalendarDate(newDate);
  };

  const { loading, audittrailReportResponse, audittrailReportErrorResponses } = useSelector(audittrailReportSelector);

  const [tableData, setTableData] = useState<any[]>(audittrailReportResponse);

  const showHandler = () => {
    dispatch(
      getAuditTrailReportRequest({
        fromDate: moment(calendarDate).format('YYYY-MM-01'),
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        toDate: moment(calendarDate).format('YYYY-MM-') + moment(calendarDate).daysInMonth(),
        actionSeverity: moduleType,
      }),
    );
  };

  const onExelDownload = () => {
    dispatch(
      getGlobalExcelReportRequest({
        fromDate: moment(calendarDate).format('YYYY-MM-01'),
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        toDate: moment(calendarDate).format('YYYY-MM-') + moment(calendarDate).daysInMonth(),
        actionSeverity: moduleType,
      }),
    );
  };

  useEffect(() => {
    if (Object.keys(audittrailReportErrorResponses).length !== 0 && audittrailReportErrorResponses?.status === 403) {
      navigate('/error');
    }
  }, [audittrailReportErrorResponses, navigate]);

  useEffect(() => {
    dispatch(
      getAuditTrailReportRequest({
        fromDate: moment(calendarDate).format('YYYY-MM-01'),
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        toDate: moment(calendarDate).format('YYYY-MM-') + moment(calendarDate).daysInMonth(),
        actionSeverity: moduleType,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
    } else {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timeoutId = setTimeout(() => {
        showHandler();
        timeoutId = null;
      }, 1000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [calendarDate]);

  return (
    <div className={styles.wrapper}>
      {Object.keys(audittrailReportErrorResponses).length !== 0 ? (
        <ErrorResponse error={audittrailReportErrorResponses} />
      ) : null}
      <div className={styles.loadingGroup}>
        <span className={styles.title}>{t('audittrailHeader')}</span>
        {loading ? <Spinner /> : null}
      </div>
      <span>{t('audittrailHeaderShortDescription')}</span>
      <div className={styles.filterBlock}>
        <div className={styles.firstRow}>
          <div className={styles.calendarItem}>
            <span>Мітка події:</span>
            <Select
              sx={selectStyle}
              onChange={(e: SelectChangeEvent<any>) => {
                setModuleType(e.target.value);
              }}
              value={moduleType}
            >
              <MenuItem sx={{ fontSize: '13px' }} value={0}>
                Всі події
              </MenuItem>
              <MenuItem sx={{ fontSize: '13px' }} value={1}>
                Штатні події
              </MenuItem>
              <MenuItem sx={{ fontSize: '13px' }} value={2}>
                Помилки користувача
              </MenuItem>
              <MenuItem sx={{ fontSize: '13px' }} value={3}>
                Помилки системи
              </MenuItem>
            </Select>
          </div>
          <div className={styles.calendarItem}>
            <span>{t('SelectPeriod')}</span>
            <div className={styles.calendarRow}>
              <LocalizationProvider dateAdapter={AdapterMoment} locale={'uk'}>
                <DatePicker
                  inputFormat="MM/YYYY"
                  value={calendarDate}
                  views={['year', 'month']}
                  onChange={changeDateCalendar}
                  renderInput={(params) => <TextField sx={calendarStyleEnabled} {...params} />}
                  PaperProps={{
                    sx: {
                      '.MuiPickersCalendarHeader-label': {
                        fontSize: '13px',
                      },
                      '& .PrivatePickersYear-yearButton': {
                        fontSize: '13px',
                      },
                      '& .MuiTypography-root': {
                        fontSize: '13px',
                      },
                    },
                  }}
                />
                <Tooltip title="Місяць назад" placement="top">
                  <img src={ArrowBackIcon} onClick={() => handleMonthChange(-1)} alt="" />
                </Tooltip>
                <Tooltip title="Місяць вперед" placement="top">
                  <img src={ArrowForwardIcon} onClick={() => handleMonthChange(1)} alt="" />
                </Tooltip>
              </LocalizationProvider>
            </div>
          </div>
          <Button variant="contained" className={styles.btn} onClick={showHandler}>
            <span className={styles.btnText}>Пошук</span>
          </Button>
        </div>
        <div className={styles.secondRow}>
          <div className={styles.searchGroup}>
            <span>{t('filterBy')}</span>
            <InputSearch setTableData={setTableData} data={audittrailReportResponse} clearOnPropsChange={true} />
          </div>
          {tableData.length !== 0 ? (
            <div className={styles.downloadWrapper}>
              <span>Скачати таблицю в Excel</span>
              {!loading ? (
                <a onClick={onExelDownload}>
                  <DownloadIcon className={styles.downloadIcon} />
                </a>
              ) : (
                <DownloadIcon className={styles.downloadIcon} />
              )}
            </div>
          ) : null}
        </div>
      </div>
      <AuditTrailReportTable data={tableData} />
    </div>
  );
};
